import { useCallback, useEffect } from 'react';
import { useTracking } from 'react-tracking';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import {
  Grid,
  Heading,
  Text,
  Button,
  useTheme,
  Badge,
} from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';

import { useCompanyNotes } from '@/components/Note';
import type { CompanyNote } from '@/components/Note';

import { useCompanyNoteModal } from '@/components/Note/components/CompanyNoteModalContext';
import { Note } from '@/components/Note/components/Note';

import Tooltip from '@/components/Tooltip';

import { Tile } from '../../../Tile';

import { InformationIcon, TooltipTextWrapper, AddButton } from './styled';

interface Props {
  companyId?: string;
  uniqueSymbol?: string;
  selectTab: (tab: 'notes') => void;
}

export const Notes = ({ companyId, uniqueSymbol, selectTab }: Props) => {
  const { openNoteModal, modalState } = useCompanyNoteModal();

  const theme = useTheme();

  const { data: notes } = useCompanyNotes(companyId);
  const canCreateNote = !!companyId && !!uniqueSymbol;
  const hasNotes = notes && notes.length > 0;
  useEffect(() => {
    if (canCreateNote && modalState === 'adding') {
      openNoteModal({ companyId, uniqueSymbol });
    }
  }, [companyId, uniqueSymbol, modalState]);

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'Note',
    companyId,
    uniqueSymbol,
  });

  const handleAddNote = useCallback(() => {
    if (!canCreateNote) {
      return;
    }
    trackEvent({
      action: 'click',
      modifier: 'add-note',
    });
    openNoteModal({ companyId, uniqueSymbol });
  }, [companyId, uniqueSymbol]);

  const handleEditNote = useCallback(
    (note: CompanyNote) => {
      if (!canCreateNote) {
        return;
      }
      openNoteModal({ note, companyId, uniqueSymbol });
    },
    [companyId, uniqueSymbol]
  );

  return (
    <Track>
      <Tile gap="16px">
        <Grid
          templateColumns="max-content max-content"
          justifyContent="space-between"
          alignItems="center"
          data-cy-id="overview-notes-header"
        >
          <Heading
            as="h3"
            typography="tinyStrong"
            color="whiteRGBA"
            opacity={0.5}
          >
            My Notes {hasNotes && <Badge>{notes.length}</Badge>}
          </Heading>
          {hasNotes ? (
            <AddButton styleType="gentleReverse" onClick={handleAddNote}>
              <PlusSolid />
              &nbsp;Add
            </AddButton>
          ) : (
            <Tooltip
              title={
                <TooltipTextWrapper>
                  <Text textAlign="center" typography="tiny" color="spaceGray">
                    Quickly jot down your insights, resources, and stories for
                    the companies you own. This can aid in recalling the initial
                    research motivation, purchase justification, or ideal
                    purchase timing.
                  </Text>
                </TooltipTextWrapper>
              }
              light
              placement="top"
              zIndex={99999}
            >
              <Grid>
                <InformationIcon fill={theme.color.whiteRGBA(0.5)} />
              </Grid>
            </Tooltip>
          )}
        </Grid>
        <Grid data-cy-id="notes" gap="8px">
          {hasNotes ? (
            <>
              <Note
                key={`note-${notes[0].id}`}
                note={notes[0]}
                editNote={handleEditNote}
              />
              <Button
                display="block"
                styleType="loud"
                onClick={() => {
                  selectTab('notes');
                  trackEvent({
                    action: 'click',
                    modifier: 'see-more',
                  });
                }}
              >
                See more
              </Button>
            </>
          ) : (
            <Button display="block" styleType="loud" onClick={handleAddNote}>
              <EditSolid /> Add note
            </Button>
          )}
        </Grid>
      </Tile>
    </Track>
  );
};
